import {createApp} from "petite-vue";
import Swiper from "swiper";
import {Navigation} from "swiper/modules";
import initArchiveOffer from "./archive-offer";
import {isInViewport} from "./utils/is-in-viewport";
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import {LightGalleryAllSettings} from "lightgallery/lg-settings";
import {initMap} from "./map";

import.meta.glob([ '@images/**' ])
import.meta.env.DEV && import("@styles/main.sass");

declare global {
    interface Window {
        mapLoaded: boolean;
    }

    interface Header {
        opened: boolean
        sticked: boolean
        up: boolean
        lastScroll: number
        handleOpenedHeader(state: boolean): void
        handleStickyHeader(): void
    }
}

const headerEl = document.querySelector<HTMLElement>('[data-header]'),
    headerScope: Header = {
        opened: false,
        sticked: false,
        up: false,
        lastScroll: 0,
        handleOpenedHeader(state) {
            document.body.classList.toggle("u-hiddenOverflow", state)
        },
        handleStickyHeader() {
            const hidingOnScroll = () => {
                const current = window.scrollY
                if (Math.abs(this.lastScroll - current) < window.innerHeight) {
                    this.sticked = current > 0
                    this.up = this.sticked && current > headerEl.offsetHeight * 2 && current > this.lastScroll
                }

                this.lastScroll = current
            }

            this.lastScroll = window.scrollY
            hidingOnScroll()

            window.addEventListener('scroll', () => hidingOnScroll(), { passive: true })
        }
    }

headerEl && createApp(headerScope).mount(headerEl);
initArchiveOffer();

(() => {
    document.querySelectorAll<HTMLVideoElement>('[data-hero-video]').forEach(video => {
        video.closest('a').addEventListener('mouseenter', () => video.play(), { once: true })
    })
})();

(() => {
    const process = document.querySelector<HTMLElement>('[data-sales-process]')
    if (!process) return

    const steps = process.querySelectorAll('[data-sales-process-step]').length
    if (steps < 2) return

    const offsetProcess = () => {
        const offsetTop = process.offsetTop,
            maxCount = (steps - 1) / Math.max(parseInt(getComputedStyle(process).getPropertyValue('--salesProcessOffsetMultiplier')) || 1, 1)

        process.style.setProperty(
            '--salesProcessOffset',
            `${-Math.min(Math.max(0, (window.scrollY - offsetTop) / window.innerHeight / maxCount), 1)}`
        )
    }

    offsetProcess()
    window.addEventListener("scroll", offsetProcess)
})();

(() => { // Count up numbers
    if (!document.querySelectorAll('[data-countable-number]')) return

    const animationDuration = 2400;
    const frameDuration = 1000 / 60;
    const totalFrames = Math.round( animationDuration / frameDuration );
    const easeOutQuad = t => t * ( 2 - t );

    const animateCountUp = (el: HTMLElement) => {
        if (el.classList.contains("is-counted"))
            return;

        el.classList.add("is-counted")
        let frame = 0;

        const isDecimal = el.textContent.includes('.'),
            decimalLength = isDecimal ? el.textContent.split('.')[1].length : 0;

        const countTo = parseFloat( el.innerText );
        const counter = setInterval( () => {
            frame++;
            const progress = easeOutQuad( frame / totalFrames ),
                currentCount = isDecimal ? ( countTo * progress ).toFixed(decimalLength)
                    : Math.round( countTo * progress );

            if ( parseFloat( el.textContent ) !== currentCount )
                el.textContent = currentCount.toLocaleString()

            frame === totalFrames && clearInterval( counter )
        }, frameDuration );
    };

    const runCountUp = () => {
        document.querySelectorAll('[data-countable-number]').forEach( (el: HTMLElement) => {
            isInViewport(el, 20) && animateCountUp(el);
        });
    }

    runCountUp();
    window.addEventListener("scroll", runCountUp);
})();

(() => {
    const swiper = document.querySelector('[data-testimonials-swiper]') as HTMLElement
    if (!swiper) return

    new Swiper(swiper, {
        modules: [Navigation],
        loop: true,
        slidesPerView: 1,
        spaceBetween: 16,
        autoHeight: true,
        breakpoints: {
            768: {
                slidesPerView: 2
            },
            1024: {
                slidesPerView: 3,
            }
        },
        navigation: {
            nextEl: '[data-testimonials-nav="next"]',
            prevEl: '[data-testimonials-nav="prev"]',
        }
    });
})();

(() => {
    const swiper = document.querySelector('[data-reference-articles-swiper]') as HTMLElement
    if (!swiper) return

    new Swiper(swiper, {
        modules: [Navigation],
        loop: true,
        slidesPerView: 1,
        spaceBetween: 16,
        autoHeight: true,
        breakpoints: {
            768: {
                slidesPerView: 2
            },
            1024: {
                slidesPerView: 3,
            }
        },
        navigation: {
            nextEl: '[data-reference-articles-nav="next"]',
            prevEl: '[data-reference-articles-nav="prev"]',
        }
    });
})();


(() => {
    const offerGallery: HTMLElement = document.querySelector("[data-offer-gallery]"),
        offerPlans: HTMLElement = document.querySelector("[data-offer-plans]"),
        contentGallery = document.querySelectorAll<HTMLElement>(".wp-block-gallery:has(a[href])");

    const defaultOptions: Partial<LightGalleryAllSettings> = {
        plugins: [lgThumbnail],
        download: false,
        enableDrag: true,
        hideScrollbar: true,
        mousewheel: true,
        thumbnail: true
    }

    offerGallery && lightGallery(offerGallery, {...defaultOptions});
    offerPlans && lightGallery(offerPlans, {...defaultOptions});
    contentGallery.forEach(gallery => {
        lightGallery(gallery, {...defaultOptions, selector: '.wp-block-image > a[href]'});
    });
})();

(() => {
    window.mapLoaded = false;

    const mapHolder: HTMLElement = document.querySelector("[data-map-holder]");
    if (!mapHolder) return;

    const loadMap = () => {
        if (window.mapLoaded || !isInViewport(mapHolder, 4))
            return;

        window.mapLoaded = true;
        initMap();
    };

    document.addEventListener("DOMContentLoaded", loadMap);
    document.addEventListener("scroll", loadMap);
})();


(() => {
    const contentTimelineSection = document.querySelector('.ContentTimeline');
    if (!contentTimelineSection) return

    const blockGroupCount = contentTimelineSection.querySelectorAll('.wp-block-group').length;

    // Nastavení CSS proměnných
    contentTimelineSection.parentElement.style.setProperty('--block-group-count', Math.ceil(blockGroupCount / 2).toString());
    contentTimelineSection.parentElement.style.setProperty('--block-group-count-respo', blockGroupCount.toString());
})();

(() => {
    const currentUrl = window.location.pathname;

    if (currentUrl === "/rubrika/pripadove-studie/") {
        const blogLinkParent = document.querySelector('#menu-item-12');
        if (blogLinkParent && blogLinkParent.classList.contains('current_page_parent')) {
            blogLinkParent.classList.remove("current_page_parent");
        }
    }
})();





